import { useCallback, useMemo } from "react";

import { AxiosError } from "axios";
import { omit, pick } from "ramda";

import { getPaths } from "core/getPaths";
import { useCompetitionStore } from "shared/contexts";
import { useShare, useToast } from "shared/hooks";
import { useCompetitionApiService } from "shared/services";
import type {
	CompetitionCreateDivisionModel,
	CompetitionGroupModel,
	CompetitionModel,
	CurrencyCode,
	EntityRoles,
	FetchCompetitionTeamsParams,
	FullTeamModel,
	GroupStatusEnum,
	IgnoreStateUpdate,
	InvitationStatus,
	LogoModel,
	MatchesFilterModel,
	MediaModel,
	NewFieldReservationModel,
	NewScoutModel,
	PaginationParamsModel,
	UpdateClubPlayerFormModal,
	UpdateFieldReservationModel
} from "shared/types";
import { BackendEntityType } from "shared/types";

import usePlatformEnv from "./usePlatformEnv";

import useWaiversService from "../services/useWaiversApiService";
import type {
	CompetitionClubsTeamsFilterModel,
	CompetitionCustomFeeModel,
	FetchCompetitionDivisionsParams,
	PlayersByTeamsFilterModel
} from "../types/CompetitionModel";
import type { PartialPaymentSettingsModel, SplitPaymentSettingsModel } from "../types/PaymentModel";
import type { UpdateScoutModel } from "../types/ScoutModel";
import type { WaiverFilterModel, WaiverModel } from "../types/Waiver";

const { competition: competitionRoutes } = getPaths();

const useCompetition = () => {
	const competitionService = useCompetitionApiService();

	const waiversService = useWaiversService();

	const store = useCompetitionStore();
	const { setState, setInitial } = useCompetitionStore();

	const { showToast } = useToast();

	const { isShowRoleToasts } = usePlatformEnv();

	const { copyLink } = useShare();

	const methods = useMemo(() => {
		return {
			findCompetitions: async (params: {
				clubId?: string;
				search?: string;
				maxMiles?: number;
				limit?: number;
				noStateUpdate?: boolean;
			}) => {
				setState({ loading: true });
				const { competitions } = await competitionService.findCompetitions(omit(["noStateUpdate"], params));

				params?.noStateUpdate ? setState({ loading: false }) : setState({ competitions, loading: false });

				return competitions;
			},
			applyToCompetition: async (params: {
				clubId: string;
				competitionId: string;
				teams?: Partial<FullTeamModel>[];
			}) => {
				setState({ submitting: true });

				try {
					await competitionService.applyToCompetition(params);
					showToast({
						text: params?.teams?.length
							? "Competition application was sent for selected team"
							: "Competition application was sent for selected club",
						noIcon: true
					});
				} catch (e) {
					console.error("Can't apply to competition: ", e);
				} finally {
					setState({ submitting: false });
				}
			},
			getStructure: async (params: { id: string } & IgnoreStateUpdate) => {
				if (!params?.ignoreLoading) {
					setState({ loadingStructure: true });
				}

				try {
					const { competition } = await competitionService.getStructure(params.id);

					if (!params?.ignoreStateUpdate) {
						setState({ structure: competition.struct });
					}

					return competition.struct;
				} catch (err) {
					console.error(err);
					showToast({
						text: "Couldn't fetch competition structure.",
						noIcon: true
					});
					return null;
				} finally {
					if (!params?.ignoreLoading) {
						setState({ loadingStructure: false });
					}
				}
			},
			getTeams: async (params: FetchCompetitionTeamsParams, ignoreStateUpdate = false) => {
				if (!ignoreStateUpdate) {
					setState({ loadingTeams: true });
				}

				try {
					const { competition } = await competitionService.getTeams(params);

					if (!ignoreStateUpdate) {
						setState({ teams: competition.teams });
					}

					return competition.teams;
				} catch (err) {
					console.error(err);
					showToast({
						text: "Couldn't fetch competition teams.",
						noIcon: true
					});
					return [];
				} finally {
					setState({ loadingTeams: false });
				}
			},
			getClubsWithTeams: async (
				params: {
					competitionId: string;
					withPayments?: boolean;
					filters?: CompetitionClubsTeamsFilterModel;
				} & IgnoreStateUpdate
			) => {
				if (!params.ignoreLoading) {
					setState({ loadingClubsWithTeams: true });
				}

				try {
					const { competition } = await competitionService.getClubsWithTeams(
						pick(["competitionId", "withPayments", "filters"], params)
					);

					if (!params.ignoreStateUpdate) {
						setState({ clubsWithTeams: competition.clubs });
					}

					return competition.clubs;
				} catch (err: unknown) {
					console.error(err);
					if (err instanceof Error && err.cause !== AxiosError.ERR_CANCELED) {
						showToast({
							text: "Couldn't fetch competition clubs with teams.",
							noIcon: true
						});
					}
					return [];
				} finally {
					if (!params.ignoreLoading) {
						setState({ loadingClubsWithTeams: false });
					}
				}
			},
			getClubs: async (params: { competitionId: string; status?: InvitationStatus }) => {
				setState({ loadingClubs: true });
				const { clubs } = await competitionService.getClubs(params);
				setState({ clubs, loadingClubs: false });
				return clubs;
			},
			getCompetitionMatches: async ({
				limit = 20,
				before,
				after,
				ignoreStateUpdate,
				ignoreLoading,
				...rest
			}: {
				competitionId: string;
				filters?: MatchesFilterModel;
			} & PaginationParamsModel &
				IgnoreStateUpdate) => {
				if (!ignoreLoading) {
					setState({ loadingMatches: true });
				}

				if (!before && !after && !ignoreStateUpdate) {
					setState({
						prevListingMatchesToken: null,
						nextListingMatchesToken: null
					});
				}

				try {
					const { matches, nextToken, previousToken } = await competitionService.getCompetitionMatches({
						limit,
						before,
						after,
						...rest
					});

					if (!ignoreStateUpdate) {
						setState(ctx => ({
							prevListingMatchesToken: after ? ctx.prevListingMatchesToken : previousToken,
							nextListingMatchesToken: before ? ctx.nextListingMatchesToken : nextToken,
							matches: !!before || !!after ? ctx.matches : matches
						}));
					}

					return {
						nextToken,
						previousToken,
						matches
					};
				} catch (err: unknown) {
					console.error(err);
					if (err instanceof Error && err.cause !== AxiosError.ERR_CANCELED) {
						showToast({
							text: "Couldn't fetch competition matches.",
							noIcon: true
						});
					}
					return {
						nextToken: null,
						previousToken: null,
						matches: []
					};
				} finally {
					if (!ignoreLoading) {
						setState({ loadingMatches: false });
					}
				}
			},
			getOverview: async (competitionId: string, ignoreLoading?: boolean, ignoreStateUpdate?: boolean) => {
				!ignoreLoading && setState({ loadingOverview: true });

				try {
					const { competition } = await competitionService.getOverview(competitionId);
					if (!ignoreStateUpdate) {
						setState({ overview: competition });
					}
					return competition;
				} catch {
					showToast({
						text: "Competition couldn't be found",
						noIcon: true
					});
					return null;
				} finally {
					if (!ignoreLoading) {
						setState({ loadingOverview: false });
					}
				}
			},
			getInfo: async (competitionId: string, ignoreLoading?: boolean) => {
				!ignoreLoading && setState({ loadingInfo: true });

				try {
					const { competition } = await competitionService.getInfo(competitionId);
					setState(ctx => ({ info: competition, loadingInfo: ignoreLoading ? ctx.loadingInfo : false }));

					if (competition?.auth?.roles && isShowRoleToasts) {
						showToast({
							text: `Roles for competition: ${competition.auth.roles.join(", ")}`,
							noIcon: true,
							position: "bottom-left"
						});
					}

					return competition;
				} catch {
					showToast({
						text: "Competition couldn't be found",
						noIcon: true
					});
					return null;
				} finally {
					if (!ignoreLoading) {
						setState({ loadingInfo: false });
					}
				}
			},
			getCompetitionReferees: async (competitionId: string) => {
				setState({ loadingReferees: true });
				const { refereeOrganizations } = await competitionService.getCompetitionReferees(competitionId);

				setState({ loadingReferees: false });

				return refereeOrganizations;
			},
			updateCompetitionSettings: async (
				updateData: Omit<CompetitionModel, "id" | "league" | "type" | "logo"> & {
					logo?: string | null | LogoModel | MediaModel;
					competitionId: string;
					refereeOrganizations?: {
						add: { id: string }[];
						remove: { id: string }[];
					};
					registrationFee?: number;
					registrationFeeCurrency?: CurrencyCode;
					customRegistrationFees?: Partial<CompetitionCustomFeeModel>[];
					partialPayment?: null | PartialPaymentSettingsModel;
					splitPayment?: SplitPaymentSettingsModel[];
				}
			) => {
				setState({ submitting: true });
				try {
					await competitionService.updateCompetitionSettings(updateData);

					if (updateData.competitionId) {
						methods.getOverview(updateData.competitionId, true);
					}

					showToast({ text: "Competition settings were successfully updated", noIcon: true });
				} catch (e) {
					console.error("Couldn't update competition settings ", e);
				} finally {
					setState({ submitting: false });
				}
			},
			listCompetitionFieldReservations: async ({
				competitionId
			}: {
				competitionId: string;
				startsAt?: Date;
				endsAt?: Date;
			}) => {
				setState({ loadingFieldReservations: true });
				try {
					const { fields } = await competitionService.listCompetitionFieldReservations(competitionId);
					setState({ fieldReservations: fields });
				} catch (e) {
					console.error("Couldn't load field reservations ", e);
				} finally {
					setState({ loadingFieldReservations: false });
				}
			},
			updateCompetitionFieldReservations: async (
				competitionId: string,
				reservations: {
					create: NewFieldReservationModel[];
					update: UpdateFieldReservationModel[];
					remove: { id: string }[];
				}
			) => {
				setState({ submitting: true });
				try {
					return await competitionService.updateCompetitionFieldReservations(competitionId, reservations);
				} catch (e) {
					console.error(e);
					return {
						created: [],
						removed: [],
						updated: []
					};
				} finally {
					setState({ submitting: false });
				}
			},
			listCompetitionFacilityFields: async (competitionId: string) => {
				setState({ loadingFacilityFields: true });
				try {
					const { fields } = await competitionService.listCompetitionFacilityFields(competitionId);
					setState({ facilityFields: fields });
				} catch (e) {
					console.error("Couldn't load competition fields: ", e);
				} finally {
					setState({ loadingFacilityFields: false });
				}
			},
			getTeamFilters: async (competitionId: string) => {
				setState({ loadingTeamFilters: true });
				const { competition } = await competitionService.getTeamFilters(competitionId);

				setState({ teamFilters: competition.filters, loadingTeamFilters: false });

				return competition.filters;
			},
			setCompetitionContactSettingsModal: (competitionContactSettingsModal?: boolean | CompetitionModel | string) => {
				setState({ competitionContactSettingsModal });
			},
			setCompetitionSettingsModal: (competitionSettingsModal?: boolean | CompetitionModel | string) => {
				setState({ competitionSettingsModal });
			},
			setCompetitionSetupModal: (competitionSetupModal?: boolean | CompetitionModel | string) => {
				setState({ competitionSetupModal });
			},
			getCompetitionGroups: async (
				data: { competitionId: string; status?: GroupStatusEnum; withTeams?: boolean },
				ignoreStateUpdates = false
			) => {
				if (!ignoreStateUpdates) {
					setState({ loadingGroups: true });
				}

				const { groups } = await competitionService.getCompetitionGroups(data);

				if (!ignoreStateUpdates) {
					setState({ groups, loadingGroups: false });
				}

				return groups;
			},
			getFollowedCompetitions: async (params: { search?: string } & PaginationParamsModel) => {
				setState({ loadingFollowedCompetitions: true });
				const { competitions, nextToken } = await competitionService.getFollowedCompetitions(params);
				setState({ followedCompetitionsNextToken: nextToken, loadingFollowedCompetitions: false });
				return competitions;
			},
			getCompetitionTeamStats: async (competitionId: string) => {
				setState({ loadingCompetitionTeamStats: true });
				const { items } = await competitionService.getCompetitionTeamStats(competitionId);

				setState({ loadingCompetitionTeamStats: false });

				return items || [];
			},
			getCompetitionPlayerStats: async (competitionId: string) => {
				setState({ loadingCompetitionPlayerStats: true });
				const { items } = await competitionService.getCompetitionPlayerStats(competitionId);

				setState({ loadingCompetitionPlayerStats: false });

				return items || [];
			},
			getCompetitionDivisions: async ({
				before,
				after,
				...rest
			}: FetchCompetitionDivisionsParams & PaginationParamsModel) => {
				setState({ loadingCompetitionDivisions: true });

				try {
					const { divisions } = await competitionService.getCompetitionDivisions({ before, after, ...rest });
					setState(ctx => ({
						divisions: !!before || !!after ? ctx.divisions : divisions
					}));
					return {
						nextToken: null,
						divisions
					};
				} catch (err) {
					console.error(err);
					showToast({ text: "Couldn't load competition divisions.", noIcon: true });
					return {
						nextToken: null,
						divisions: []
					};
				} finally {
					setState({ loadingCompetitionDivisions: false });
				}
			},
			getCompetitionDivision: async (divisionId: string, stateOptions?: IgnoreStateUpdate) => {
				if (!stateOptions?.ignoreStateUpdate) {
					setState({ loadingCompetitionDivision: true });
				}

				try {
					const { division } = await competitionService.getCompetitionDivision(divisionId);
					return division;
				} catch (err) {
					console.error(err);
					showToast({ text: "Couldn't load competition division.", noIcon: true });
					return null;
				} finally {
					if (!stateOptions?.ignoreStateUpdate) {
						setState({ loadingCompetitionDivision: false });
					}
				}
			},
			createDivisions: async (competitionId: string, divisions: CompetitionCreateDivisionModel[]) => {
				setState({ submitting: true });

				try {
					const { divisions: createdDivisions } = await competitionService.createDivisions(competitionId, divisions);
					showToast({ text: "Competition divisions were successfully created", noIcon: true });
					return createdDivisions;
				} catch (err) {
					console.error(err);
					showToast({ text: "Couldn't crew new competition divisions.", noIcon: true });
					return [];
				} finally {
					setState({ submitting: false });
				}
			},
			updateDivision: async (competitionId: string, division: CompetitionCreateDivisionModel) => {
				setState({ submitting: true });

				try {
					const { division: updatedDivision } = await competitionService.updateDivision(competitionId, division);
					showToast({ text: "Competition division was successfully updated", noIcon: true });
					return updatedDivision;
				} catch (err) {
					console.error(err);
					showToast({ text: "Couldn't updated competition division.", noIcon: true });
					return null;
				} finally {
					setState({ submitting: false });
				}
			},
			deleteDivision: async (competitionId: string, divisionId: string) => {
				setState({ deleting: true });

				try {
					const { division } = await competitionService.deleteDivision(competitionId, divisionId);
					showToast({ text: "Competition division was successfully deleted", noIcon: true });
					return division.id;
				} catch (err) {
					console.error(err);
					showToast({ text: "Couldn't delete competition division.", noIcon: true });
					return null;
				} finally {
					setState({ deleting: false });
				}
			},
			setUserCompetitionRoles: (userCompetitionRoles: EntityRoles[]) => {
				setState({ userCompetitionRoles });
			},
			setGroups: (groups: CompetitionGroupModel[]) => {
				setState({ groups });
			},
			removeTeamsInGroup: (groupId: string, teamIds: string[]) => {
				setState(ctx => ({
					groups: ctx.groups.map(x =>
						x.id === groupId
							? {
									...x,
									teams: x.teams.filter(t => !teamIds.some(tr => tr === t.id))
								}
							: x
					)
				}));
			},
			updateStatusOfGroup: (groupId: string, status: GroupStatusEnum) => {
				setState(ctx => ({
					groups: ctx.groups.map(x => (x.id === groupId ? { ...x, status } : x))
				}));
			},
			getFavoriteCompetitionDivisions: async () => {
				setState({ loadingFavoriteCompetitionDivisions: true });

				try {
					const { competition, competitions, divisions, groups } =
						await competitionService.getFavoriteCompetitionDivisions();
					setState({
						favoriteCompetition: competition,
						favoriteCompetitions: competitions,
						favoriteCompetitionDivisions: divisions,
						favoriteCompetitionGroups: groups
					});
				} catch (err) {
					console.error(err);
					showToast({ text: "Couldn't load favorite competition divisions.", noIcon: true });
				} finally {
					setState({ loadingFavoriteCompetitionDivisions: false });
				}
			},
			listScouts: async (competitionId: string) => {
				setState({ loadingScouts: true });

				try {
					const { competition } = await competitionService.listScouts(competitionId);
					setState({ scouts: competition.scouts });
					return competition.scouts;
				} catch (err) {
					console.error(err);
					showToast({ text: "Couldn't get scouts of competition.", noIcon: true });
				} finally {
					setState({ loadingScouts: false });
				}
			},
			updateScouts: async (
				competitionId: string,
				scouts: {
					add?: NewScoutModel[];
					remove?: { id: string }[];
					update?: UpdateScoutModel[];
				}
			) => {
				setState({ updatingScouts: true });

				try {
					const { competition } = await competitionService.updateScouts(competitionId, scouts);

					if (competition?.id) {
						if (scouts.add?.length) {
							showToast({
								text: `${scouts.add.length > 1 ? scouts.add.length : ""} Scout${
									scouts.add.length > 1 ? "s were" : " was"
								} successfully created.`,
								noIcon: true
							});
						}
						if (scouts.remove?.length) {
							showToast({
								text: `${scouts.remove.length > 1 ? scouts.remove.length : ""} Scout${
									scouts.remove.length > 1 ? "s were" : " was"
								} successfully removed.`,
								noIcon: true
							});
						}
						if (scouts.update?.length) {
							showToast({
								text: `${scouts.update.length > 1 ? scouts.update.length : ""} Scout${
									scouts.update.length > 1 ? "s were" : " was"
								} successfully updated.`,
								noIcon: true
							});
						}
					}
				} catch (err) {
					console.error(err);
					showToast({ text: "Couldn't update competition scouts.", noIcon: true });
				} finally {
					setState({ updatingScouts: false });
				}
			},
			inviteScouts: async (competitionId: string, emails: string[]) => {
				setState({ updatingScouts: true });

				try {
					const { invitations } = await competitionService.inviteScouts(competitionId, emails);
					showToast({ text: "Competition invitations were sent to the selected scouts", noIcon: true });
					return invitations;
				} catch (err) {
					console.error(err);
					showToast({ text: "Couldn't invite scouts to competition.", noIcon: true });
					return [];
				} finally {
					setState({ updatingScouts: false });
				}
			},
			updateTeams: async (
				competitionId: string,
				teams: {
					remove: { id: string }[];
				}
			) => {
				setState({ updatingTeams: true });

				try {
					const { competition } = await competitionService.updateTeams(competitionId, teams);

					if (competition?.id) {
						if (teams.remove.length) {
							showToast({
								text: `${teams.remove.length > 1 ? teams.remove.length : ""} Team${
									teams.remove.length > 1 ? "s were" : " was"
								} successfully removed.`,
								noIcon: true
							});
						}
					}
				} catch (err) {
					console.error(err);
					showToast({ text: "Couldn't update competition teams.", noIcon: true });
				} finally {
					setState({ updatingTeams: false });
				}
			},
			getPlayers: async (competitionId: string, stateOptions?: IgnoreStateUpdate) => {
				if (!stateOptions?.ignoreLoading) {
					setState({ loadingPlayers: true });
				}

				try {
					const { competition } = await competitionService.getPlayers(competitionId);

					if (!stateOptions?.ignoreStateUpdate) {
						setState({ players: competition.players });
					}

					return competition.players;
				} catch (err) {
					console.error(err);
					showToast({
						text: "Couldn't fetch competition players.",
						noIcon: true
					});
					return [];
				} finally {
					setState({ loadingPlayers: false });
				}
			},
			getPlayersByTeams: async ({
				ignoreStateUpdate,
				ignoreLoading,
				limit = 50,
				before,
				after,
				...rest
			}: {
				competitionId: string;
				slim?: boolean;
				filters?: Partial<PlayersByTeamsFilterModel>;
				withPayments?: boolean;
			} & IgnoreStateUpdate &
				PaginationParamsModel) => {
				if (!ignoreLoading) {
					setState({ loadingPlayersInTeams: true });
				}

				if (!before && !after && !ignoreStateUpdate) {
					setState({
						prevListingTeamPlayersToken: null,
						nextListingTeamPlayersToken: null
					});
				}

				try {
					const { competition, previousToken, nextToken } = await competitionService.getPlayersByTeams({
						limit,
						before,
						after,
						...rest
					});

					if (!ignoreStateUpdate) {
						setState(ctx => ({
							prevListingTeamPlayersToken: after ? ctx.prevListingTeamPlayersToken : previousToken,
							nextListingTeamPlayersToken: before ? ctx.nextListingTeamPlayersToken : nextToken,
							teamPlayers: !!before || !!after ? ctx.teamPlayers : competition.teams
						}));
					}

					return {
						teamPlayers: competition.teams,
						previousToken,
						nextToken
					};
				} catch (err: unknown) {
					console.error(err);
					if (err instanceof Error && err.cause !== AxiosError.ERR_CANCELED) {
						showToast({
							text: "Couldn't fetch competition players.",
							noIcon: true
						});
					}
					return {
						teamPlayers: [],
						previousToken: null,
						nextToken: null
					};
				} finally {
					if (!ignoreLoading) {
						setState({ loadingPlayersInTeams: false });
					}
				}
			},
			getTeamPlayers: async (params: {
				competitionId: string;
				teamId: string;
				filters?: PlayersByTeamsFilterModel;
				withPayments?: boolean;
			}) => {
				try {
					const { competition } = await competitionService.getTeamPlayers(params);

					return competition.team.players;
				} catch (err) {
					console.error(err);
					showToast({
						text: "Couldn't fetch competition team players.",
						noIcon: true
					});
					return [];
				}
			},
			deleteCompetition: async (competitionId: string) => {
				try {
					setState({ submitting: true });

					await competitionService.deleteCompetition(competitionId);
					showToast({ text: "Competition was successfully deleted", noIcon: true });
					return true;
				} catch (err) {
					console.error(err);
					showToast({
						text: "Couldn't delete competition.",
						noIcon: true
					});
					return false;
				} finally {
					setState({ submitting: false });
				}
			},
			generateOpenRegistrationPaymentOrder: async (params: {
				competitionId: string;
				players?: { id: string }[];
				teams?: { id: string; clubId: string }[];
				isPayFullAmount?: boolean;
			}) => {
				try {
					setState({ submitting: true });
					const { order } = await competitionService.generateOpenRegistrationPaymentOrder(params);
					return order;
				} catch (err) {
					console.error(err);
					showToast({
						text: "Couldn't generate open registration payment order for competition.",
						noIcon: true
					});
					return null;
				} finally {
					setState({ submitting: false });
				}
			},
			getAudiences: async (competitionId: string) => {
				try {
					setState({ loadingAudiences: true });
					const { competition } = await competitionService.getAudiences(competitionId);
					setState({ audiences: competition.audiences });
					return competition.audiences;
				} catch (err) {
					console.error(err);
					showToast({
						text: "Couldn't get competition open registration audiences.",
						noIcon: true
					});
					return [];
				} finally {
					setState({ loadingAudiences: false });
				}
			},
			appendToOpenRegistration: async (params: {
				competitionId: string;
				players?: { id: string }[];
				teams?: { id: string; clubId: string; suggestedLevelId?: null | string }[];
			}) => {
				try {
					setState({ submitting: true });
					await competitionService.appendToOpenRegistration(params);
				} catch (err) {
					console.error(err);
					showToast({
						text: "Couldn't append players/teams to competition.",
						noIcon: true
					});
					return null;
				} finally {
					setState({ submitting: false });
				}
			},
			getLinkToOpenRegistration: async (competition: Partial<CompetitionModel>, dontCopyToClipboard = false) => {
				const link = await copyLink({
					customLink: `${competitionRoutes.getPath()}/${competition.id}?registerNow=true`,
					socialMetaTagInfo: {
						socialTitle: `${competition?.name}`,
						socialDescription: "Register now for this competition on Tapin Sports"
					},
					analyticsInfo: {
						googlePlayAnalytics: {
							utmCampaign: "share",
							utmMedium: "link",
							utmTerm: "sports",
							utmSource: "link",
							utmContent: "share link"
						}
					},
					dontCopyToClipboard
				});
				return link;
			},
			acceptTeams: async (params: {
				competitionId: string;
				club: { id: string; teams?: { id: string; customFee?: number }[] };
				currency?: CurrencyCode;
				partialPayment: null | PartialPaymentSettingsModel;
				feeTaxPaidByClient?: boolean;
			}) => {
				try {
					setState({ submitting: true });
					await competitionService.acceptTeams(params);
				} catch (err) {
					console.error(err);
					showToast({
						text: "Couldn't accept teams to competition.",
						noIcon: true
					});
					return null;
				} finally {
					setState({ submitting: false });
				}
			},
			rejectTeams: async (params: { competitionId: string; teams?: { id: string }[] }) => {
				try {
					setState({ submitting: true });
					await competitionService.rejectTeams(params);
				} catch (err) {
					console.error(err);
					showToast({
						text: "Couldn't reject teams to competition.",
						noIcon: true
					});
					return null;
				} finally {
					setState({ submitting: false });
				}
			},
			listWaivers: async ({
				competitionId,
				after,
				ignoreStateUpdate,
				...rest
			}: { competitionId: string; filters?: WaiverFilterModel } & PaginationParamsModel & IgnoreStateUpdate) => {
				setState({ loadingWaivers: true });

				try {
					const { waivers, nextToken } = await waiversService.listEntityWaivers({
						after,
						objectId: competitionId,
						objectType: BackendEntityType.SoccerCompetition,
						...rest
					});
					if (!ignoreStateUpdate) {
						setState(ctx => ({
							nextListingWaiversToken: nextToken,
							waivers: !!after ? ctx.waivers : waivers || []
						}));
					}

					return {
						nextToken,
						waivers: waivers || []
					};
				} catch (err: unknown) {
					console.error(err);
					if (err instanceof Error && err.cause !== AxiosError.ERR_CANCELED) {
						showToast({
							text: "Couldn't get waivers of competition.",
							noIcon: true
						});
					}
					return {
						waivers: [],
						nextToken: null
					};
				} finally {
					setState({ loadingWaivers: false });
				}
			},
			listWaiverSignatures: async (params: { waiverId: string } & PaginationParamsModel & WaiverFilterModel) => {
				try {
					const { waiver } = await waiversService.listWaiverSignatures(params);

					return {
						signatures: waiver.signatures,
						nextToken: waiver.nextToken
					};
				} catch (err: unknown) {
					console.error(err);
					if (err instanceof Error && err.cause !== AxiosError.ERR_CANCELED) {
						showToast({
							text: "Couldn't get waiver signatures.",
							noIcon: true
						});
					}
					return {
						signatures: [],
						nextToken: null
					};
				}
			},
			sendWaiverSignatureReminder: async (params: { competitionId: string } | { signatureId: string }) => {
				try {
					const { success } = await waiversService.sendWaiverSignatureReminder(
						"competitionId" in params
							? {
									objectId: params.competitionId,
									objectType: BackendEntityType.SoccerCompetition
								}
							: params
					);
					showToast({ text: "Waiver signature reminder sent", noIcon: true });

					return success;
				} catch (err) {
					console.error(err);
					showToast({ text: "Couldn't send waiver signature reminder", noIcon: true });
					return false;
				}
			},
			createWaiver: async (waivers: Partial<WaiverModel>[]) => {
				setState({ submitting: true });

				try {
					const { waivers: newWaivers } = await waiversService.createSoccerWaiver(waivers);
					showToast({
						text: newWaivers.length === 1 ? "Waiver was successfully created" : "Waivers were successfully created",
						noIcon: true
					});
					return newWaivers;
				} catch (err) {
					console.error(err);
					showToast({ text: "Couldn't crew new competition waiver.", noIcon: true });
					return null;
				} finally {
					setState({ submitting: false });
				}
			},
			removeWaiver: async (waiverId: string) => {
				setState({ submitting: true });

				try {
					const { success } = await waiversService.removeSoccerWaiver(waiverId);
					showToast({ text: "Waiver was successfully removed", noIcon: true });
					return success;
				} catch (err) {
					console.error(err);
					showToast({ text: "Couldn't remove the competition waiver.", noIcon: true });
					return false;
				} finally {
					setState({ submitting: false });
				}
			},
			updateClubPlayers: async (params: {
				competitionId: string;
				clubId: string;
				players: {
					update: (Omit<UpdateClubPlayerFormModal, "teams"> & {
						teams: { update?: { id: string; jerseyNumber?: number }[] };
					})[];
				};
			}) => {
				setState({ loading: true });

				try {
					await competitionService.updateClubPlayers(params);
				} catch (error) {
					console.log("Update players failed", error);
				} finally {
					setState({ loading: false });
				}
			},
			getHealthDashboard: async (params: { id: string; matchOverdueTime?: number }) => {
				setState({ loadingHealthDashboard: true });

				try {
					const { competition } = await competitionService.getHealthDashboard(params);

					setState({ healthDashboard: competition });

					return competition;
				} catch (err) {
					console.error(err);
					showToast({
						text: "Couldn't fetch competition health dashboard.",
						noIcon: true
					});
					return null;
				} finally {
					setState({ loadingHealthDashboard: false });
				}
			},
			getMatchesHealthDashboard: async (params: { id: string; matchOverdueTime?: number }) => {
				setState({ loadingMatchesHealthDashboard: true });

				try {
					const { competition } = await competitionService.getMatchesHealthDashboard(params);

					setState(ctx => ({ healthDashboard: { ...ctx.healthDashboard, matches: competition.matches } }));

					return competition;
				} catch (err) {
					console.error(err);
					showToast({
						text: "Couldn't fetch competition matches health dashboard.",
						noIcon: true
					});
					return null;
				} finally {
					setState({ loadingMatchesHealthDashboard: false });
				}
			},
			publishEliminationMatches: async (params: { id: string; divisionId: string }) => {
				try {
					const { division } = await competitionService.publishEliminationMatches(params);
					return division.affectedMatchesCount;
				} catch (err) {
					console.error(err);
					showToast({
						text: "Couldn't publish elimination matches for division.",
						noIcon: true
					});
					return null;
				}
			},
			resetStore() {
				setInitial();
			}
		};
	}, [competitionService, waiversService, setState, setInitial, showToast, isShowRoleToasts, copyLink]);

	const getData = useCallback(() => {
		return store;
	}, [store]);

	return { ...methods, getData };
};

export default useCompetition;
